/**
 * Time in Hapi, as a leaderboard everybody can see.
 *
 * Minutes actually spent in the app — a visible tab somebody has touched in
 * the last few minutes — totalled for today, this week or this month. The
 * whole studio is on it, including anybody at nought, and you are marked so
 * you can find yourself at a glance.
 */

import { Clock, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { api } from '../lib/api'
import { cn } from '../lib/cn'
import { Avatar } from './Avatar'
import { BentoCard } from './BentoCard'
import { SegmentedControl } from './FilterBar'

type Period = 'today' | 'week' | 'month'

interface Standing {
  person_id: string
  name: string
  minutes: number
}

const PERIOD_LABEL: Record<Period, string> = { today: 'Today', week: 'Week', month: 'Month' }

/** "3h 05m", "42m", "0m". */
export function hoursAndMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h === 0 ? `${m}m` : `${h}h ${String(m).padStart(2, '0')}m`
}

export function TimeLeaderboard({ className, delay }: { className?: string; delay?: number }) {
  const { directory } = useAppData()
  const { currentUser } = useAuth()
  const [period, setPeriod] = useState<Period>('week')
  const [rows, setRows] = useState<Standing[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    const load = () =>
      api
        .get<Standing[]>('/time/leaderboard', { period })
        .then((r) => {
          if (!live) return
          setRows(r)
          setFailed(false)
        })
        .catch(() => live && setFailed(true))
    void load()
    // The minutes move while the page is open; a refresh a minute keeps up.
    const timer = window.setInterval(load, 60_000)
    return () => {
      live = false
      window.clearInterval(timer)
    }
  }, [period])

  const top = Math.max(1, ...(rows ?? []).map((r) => r.minutes))
  const colorOf = (id: string) => directory.find((d) => d.id === id)?.color

  return (
    <BentoCard
      title="Leaderboard"
      description="Time spent in Hapi"
      {...(className !== undefined ? { className } : {})}
      {...(delay !== undefined ? { delay } : {})}
      action={
        <SegmentedControl
          value={period}
          onChange={(v) => setPeriod(v as Period)}
          options={(Object.keys(PERIOD_LABEL) as Period[]).map((p) => ({ value: p, label: PERIOD_LABEL[p] }))}
        />
      }
    >
      {failed && rows === null ? (
        <p className="py-6 text-center text-sm text-canvas-fg/60">The leaderboard could not be loaded just now.</p>
      ) : rows === null ? (
        <p className="py-6 text-center text-sm text-canvas-fg/60">Counting…</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {rows.map((r, i) => {
            const me = currentUser?.id === r.person_id
            const leader = i === 0 && r.minutes > 0
            return (
              <li
                key={r.person_id}
                className={cn('flex items-center gap-2.5 rounded-xl px-2 py-1', me && 'bg-brand-500/10 ring-1 ring-brand-500/30')}
              >
                <span className="flex w-6 shrink-0 justify-center text-sm font-semibold tabular-nums text-canvas-fg/60">
                  {leader ? <Trophy size={16} className="text-amber-500" aria-label="First" /> : i + 1}
                </span>
                <Avatar name={r.name} {...(colorOf(r.person_id) !== undefined ? { color: colorOf(r.person_id)! } : {})} size="xs" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-canvas-fg">
                      {r.name}
                      {me && <span className="ml-1.5 text-xs font-normal text-canvas-fg/60">(you)</span>}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-sm font-semibold tabular-nums text-canvas-fg">
                      <Clock size={13} className="text-canvas-fg/50" />
                      {hoursAndMinutes(r.minutes)}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-canvas-fg/10">
                    <div
                      className={cn('h-full rounded-full', leader ? 'bg-amber-500' : 'bg-brand-500')}
                      style={{ width: `${(r.minutes / top) * 100}%` }}
                    />
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </BentoCard>
  )
}
